import { lazy, type ComponentType } from "react";
import { withRetry } from "./withRetry";

type LazyFactory<T = unknown> = () => Promise<{ default: ComponentType<T> }>;

export const NEW_VERSION_EVENT = "app:new-version-available";

let notified = false;

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message.toLowerCase();
  return (
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("unable to preload css")
  );
}

/**
 * Drop-in replacement for React.lazy that handles stale-chunk errors after deploys.
 *
 * 1. Retries the dynamic import once (transient network failures).
 * 2. If the error is a chunk-load failure (hash mismatch after deploy),
 *    dispatches a one-time `app:new-version-available` window event so the
 *    host app can show a confirmation modal asking the user to refresh.
 * 3. If the user is offline, holds Suspense and waits for the `online` event
 *    before reloading — reloading while offline produces the native browser
 *    offline page (e.g. Chrome dinosaur).
 * 4. Returns a never-resolving promise on chunk failure so Suspense keeps
 *    showing the current fallback instead of crashing.
 */
export function lazyWithRetry<T = unknown>(factory: LazyFactory<T>) {
  return lazy(async () => {
    try {
      return await withRetry(factory, { maxRetries: 1, baseDelayMs: 400 });
    } catch (error) {
      if (isChunkLoadError(error)) {
        const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
        if (isOffline) {
          if (typeof window !== "undefined") {
            const reloadWhenOnline = (): void => {
              window.removeEventListener("online", reloadWhenOnline);
              window.location.reload();
            };
            window.addEventListener("online", reloadWhenOnline);
          }
          return new Promise<never>(() => {});
        }
        if (!notified && typeof window !== "undefined") {
          notified = true;
          window.dispatchEvent(new CustomEvent(NEW_VERSION_EVENT));
        }
        return new Promise<never>(() => {});
      }
      throw error;
    }
  });
}
